/**
 * RBAC — the single role→capability matrix, shared by api (built pkg), web
 * (source alias), and mcp so the three surfaces can never drift.
 *
 * Roles live in the infras-local `app_roles` table (email → role); the staff-auth
 * JWT `root` claim only BOOTSTRAPS the first root row. Unknown users default to
 * `new_member` (onboarding surface only: docs + Get Started + PAT mint).
 *
 * Secret levels keep the DB names from 010_key_levels ('critical'/'restricted'/
 * 'normal'); the UI displays them as L0/L1/L3 (org language — L2 reserved).
 * Visibility: root sees all, maintainer L1+L3, engineer L3, new_member none.
 */

export type AppRole = 'root' | 'maintainer' | 'engineer' | 'new_member';

export type SecretLevel = 'critical' | 'restricted' | 'normal';

export const APP_ROLES: AppRole[] = ['root', 'maintainer', 'engineer', 'new_member'];

export const SECRET_LEVELS: SecretLevel[] = ['critical', 'restricted', 'normal'];

/** Display badges — org language. L2 is reserved/unused. */
export const SECRET_LEVEL_LABELS: Record<SecretLevel, string> = {
  critical: 'L0',
  restricted: 'L1',
  normal: 'L3',
};

/** Which secret-inventory rows (names/metadata) each role may see. */
export const ROLE_ALLOWED_LEVELS: Record<AppRole, SecretLevel[]> = {
  root: ['critical', 'restricted', 'normal'],
  maintainer: ['restricted', 'normal'],
  engineer: ['normal'],
  new_member: [],
};

export const canSeeInventory = (role: AppRole): boolean => role !== 'new_member';

export const canEditSecretLevels = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

export const canAssignRoles = (role: AppRole): boolean => role === 'root';

/**
 * Curate the help-centre URL on a customer error code. root|maintainer — the same
 * bar as every other curated field, and for the same reason here: a doc_url is
 * PUBLISHED to customers on the emitted event, so a bad one is a bad link in a
 * customer's error message, not just a wrong row in an internal table.
 */
export const canEditErrorCodeDocs = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * Repo ownership curation.
 *   - Owner (accountable stakeholder) is assigned only by app root|maintainer — the
 *     "find the owner first" step is ours.
 *   - Maintainer (incident contact) is assigned by app root|maintainer OR by the
 *     repo's own Owner (delegation — the owner adds their maintainer afterwards).
 * Lower roles never self-serve: the only elevated path for a non-root/maintainer is
 * already being that repo's Owner.
 */
export const canAssignRepoOwner = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

export const canAssignRepoMaintainer = (role: AppRole, callerIsRepoOwner: boolean): boolean =>
  canAssignRepoOwner(role) || callerIsRepoOwner;

/**
 * Curate a repo's CLASSIFICATION (product line / kind / service). root|maintainer — the same
 * privileged tier that assigns owners. The ONE canonical rule: the dashboard, the MCP tool and
 * the resolver all call this, so "who may classify" can never drift between surfaces again
 * (it used to: the dashboard was root-only while MCP allowed maintainer). Decided 2026-08.
 */
export const canClassifyRepo = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * Engineers ("who do I pull in to work on this") are staffed by the same people
 * who staff Maintainers — root|maintainer, or the repo's own Owner delegating.
 * Deliberately NOT also the repo's existing Maintainers: the roster would then be
 * able to grow itself with neither an Owner nor an admin in the loop.
 */
export const canAssignRepoEngineer = canAssignRepoMaintainer;

/**
 * Access-request workflow: see/act on the full review queue + manage the
 * reviewer contact list and question templates. Assignee-scoped actions
 * (decide/escalate on requests you're assigned to) don't need this.
 */
export const canReviewAccessRequests = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * Decide (approve/deny) a request you filed YOURSELF. Everyone else is refused —
 * four-eyes is the default and stays the default.
 *
 * Root is exempt because for root the guard was never a control, only a detour:
 * approving RECORDS a grant that happened out-of-band, and root already holds the
 * power to make that grant, to appoint a reviewer, or to change roles. So the rule
 * only forced root to bother a second person to rubber-stamp their own note.
 * Root self-deciding is a self-documented grant ("a note to myself").
 *
 * The trade is auditability, so it is NOT silent: a self-decision stamps
 * `selfDecided` on the timeline event and is called out in the UI + Slack, which
 * makes it greppable and obvious to a reviewer that no second pair of eyes saw it.
 */
export const canDecideOwnAccessRequest = (role: AppRole): boolean => role === 'root';

/**
 * Cancel a request you did NOT file. Cancelling is withdrawal, not a decision —
 * the requester owns it — but root needs it for housekeeping (a requester who
 * left, or an abandoned thread nobody will decide). Deliberately root-only:
 * maintainers decide (approve/deny), they don't withdraw someone else's ask.
 */
export const canCancelAnyAccessRequest = (role: AppRole): boolean => role === 'root';

/**
 * Agent control plane: draft/plan/cancel commands against live clusters and see the
 * command queue. Mirrors canReviewAccessRequests.
 */
export const canControlAgents = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * APPROVAL is data-dependent, so it cannot be expressed as a decorator: a maintainer
 * may approve read-only ops anywhere and mutating ops on non-production clusters, but a
 * MUTATING op on a PRODUCTION cluster is root-only. This is the role-layer half of
 * "never auto-apply/deploy anything production-related"; the other half is the
 * draft→approve split plus the plan digest.
 */
export const canApproveAgentCommand = (
  role: AppRole,
  cmd: { mutating: boolean; production: boolean },
): boolean => role === 'root' || (role === 'maintainer' && !(cmd.mutating && cmd.production));

/**
 * Command output can contain pod logs (env dumps, tokens in stack traces), so it is
 * gated at the same tier as secret levels and is NOT part of the command object.
 */
export const canReadCommandOutput = (role: AppRole): boolean =>
  role === 'root' || role === 'maintainer';

/**
 * MCP tools a new_member may call — enough to verify their PAT + client wiring
 * (catalog = deploy-recipe docs; search is level-filtered so secret docs never
 * surface for an empty allowed-levels set) PLUS the access-request tools: new
 * members are exactly the people who need to request access. Per-request
 * visibility is enforced in the handlers (mine-scope, assertRequestVisible), not
 * by this allowlist, so listing/reading here never leaks another user's request.
 */
export const NEW_MEMBER_MCP_TOOLS = [
  'search',
  'list_catalog',
  'get_catalog',
  'create_access_request',
  'list_access_requests',
  'get_access_request',
  'comment_access_request',
  'edit_access_request',
  'cancel_access_request',
];

/**
 * MCP tools that WRITE curated data and are gated to root|maintainer (mirrors
 * canEditSecretLevels). Everything else stays read-only-for-all / recommend-only.
 * These writes are hidden from an engineer's tools/list (the handlers also refuse
 * them defensively): `set_key_note` sets a config key's markdown description;
 * `update_repo` curates a repo's product line, service and Owner/Maintainer; and
 * `bulk_assign_owner` assigns one person across every repo matching a filter.
 *
 * NOTE `update_repo` is gated STRICTER here than the underlying rule: a repo's own
 * Owner may assign that repo's Maintainer (`canAssignRepoMaintainer` delegation), but
 * only from the dashboard — an engineer never sees the tool at all. Same shape as
 * `cancel_access_request` exposing only the self-cancel half of its shared function:
 * the MCP surface is deliberately narrower than the UI, never wider.
 */
/**
 * NOTE the default at the bottom of mcpToolAllowed is `return true` — a tool NOT listed
 * here is callable by engineers. Anything dangerous must be added explicitly.
 * `plan_agent_command` / `draft_agent_command` render a diff and create a DRAFT; they can
 * never dispatch. There is deliberately **no approve_agent_command tool** — dispatching a
 * command into a live cluster stays a human action in the dashboard, exactly as
 * access-requests has no `decide` tool.
 */
export const MAINTAINER_MCP_TOOLS = [
  'set_key_note',
  'update_repo',
  'bulk_assign_owner',
  'plan_agent_command',
  'draft_agent_command',
];

export function mcpToolAllowed(role: AppRole, tool: string): boolean {
  if (MAINTAINER_MCP_TOOLS.includes(tool)) return role === 'root' || role === 'maintainer';
  if (role === 'new_member') return NEW_MEMBER_MCP_TOOLS.includes(tool);
  return true;
}
